import { z } from 'zod';
import { boardParams, boardRole, endpoint, id, LIMITS, title } from './common';

// Boards and their sections.

const section = z.object({ id: z.string(), title: z.string(), taskIds: z.array(z.string()).default([]) }).passthrough();

// A board as stored. Only what the app reads is listed; anything else the server includes passes through.
const boardDoc = z.object({
    id: z.string(),
    title: z.string(),
    ownerUserId: z.string().optional(),
    conversationId: id.optional(),
    sectionOrder: z.array(z.string()).optional(),
    sections: z.record(section).optional(),
    kioskMessage: z.string().optional(),
    kioskPhotoUrls: z.array(z.string()).optional(),
    archiveAfterDays: z.number().optional(),
}).passthrough();

export const createBoard = endpoint({
    method: 'POST',
    path: '/createBoard',
    body: z.object({ boardTitle: title('The board title') }),
    response: z.object({ boardId: id }),
});

// A board in the picker: the board, the viewer's role on it, and how far along its tasks are.
export const boardSummary = boardDoc.extend({
    viewerRole: boardRole.nullable(),
    taskCount: z.number(),
    completedCount: z.number(),
});
export type BoardSummary = z.infer<typeof boardSummary>;

export const getBoards = endpoint({
    method: 'GET',
    path: '/getBoards',
    response: z.object({
        boards: z.array(boardSummary),
        // Set when the account is locked to one board's kiosk display.
        kioskBoardId: z.string().nullable(),
    }),
});

export const getBoard = endpoint({
    method: 'GET',
    path: '/getBoard/:boardId',
    params: boardParams,
    response: z.object({
        board: boardDoc,
        archiveAfterDays: z.number(),
        viewerRole: boardRole.optional(),
        isKiosk: z.boolean(),
    }),
});

export const updateBoardTitle = endpoint({
    method: 'POST',
    path: '/updateBoardTitle',
    body: z.object({ boardId: id, boardTitle: title('The board title') }),
    response: z.object({ boardId: id }),
});

export const updateArchiveSettings = endpoint({
    method: 'POST',
    path: '/updateArchiveSettings',
    body: z.object({
        boardId: id,
        archiveAfterDays: z.number().int().min(0).max(LIMITS.archiveAfterDays, `archiveAfterDays must be a whole number from 0 to ${LIMITS.archiveAfterDays}`),
    }),
    response: z.object({ boardId: id, archiveAfterDays: z.number() }),
});

export const createSection = endpoint({
    method: 'POST',
    path: '/createSection',
    body: z.object({ boardId: id, sectionTitle: title('The section title') }),
    response: z.object({ sectionId: id }),
});

export const updateSectionTitle = endpoint({
    method: 'POST',
    path: '/updateSectionTitle',
    body: z.object({ boardId: id, sectionId: id, sectionTitle: title('The section title') }),
    response: z.object({ sectionId: id }),
});

export const deleteSection = endpoint({
    method: 'POST',
    path: '/deleteSection',
    body: z.object({ boardId: id, sectionId: id }),
    response: z.object({ sectionId: id }),
});
